/**
 * Checkout Page component
 * @returns <CheckoutPage />
 */

// Import necessary libraries, components and hooks
import { useLocation } from "react-router-dom";
import { useGetCartQuery } from "../redux/apiSlice";
import { formatPrice } from "../scripts/helpers/formatPrice";
import { sumQuantityInCart } from "../scripts/helpers/cartFunctions";
import { AnimatePresence } from "framer-motion";

// Import custom components
import Breadcrumbs from "../components/Breadcrumbs";
import TitleAndButton from "../components/TitleAndButton";
import Button from "../components/Button";
import CartAddedItemContainer from "../components/CartAddedItemContainer";
import LegalInformation from "../components/LegalInformation";
import CheckoutForm from "../components/CheckoutForm";
import Loader from "../components/Loader";

function checkForTypes(objects) {
    return objects.some(
        (obj) => obj.type === "accessory" || obj.type === "merchandise"
    );
}

// CheckoutPage component
const CheckoutPage = () => {
    // Get the current location to use for Breadcrumbs
    const location = useLocation();

    // Get the cart data using useGetCartQuery custom hook
    const {
        data: cart,
        isFetching,
        isLoading,
        isSuccess,
        isError,
    } = useGetCartQuery();

    // Extract the cart items, total and item quantity from the cart
    const cartItems = cart?.items;
    const cartTotal = cart?.total;
    const cartItemsQuantity = cartItems && sumQuantityInCart(cartItems);

    // Discounts
    const hasDiscounts = cart?.discount?.percentage > 0 && cart?.discount?.price > 0

    // Define the BreadcrumbsProps to be passed to the Breadcrumbs component
    const BreadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio",
            },
            {
                link: location.pathname,
                label: "Finaliza tu compra",
            },
        ],
    };

    const data = {
        legalInfo: {
            texts: [
                "(*) Precios sugeridos.Valores de consumo de combustible han sido obtenidos en mediciones de laboratorio según ciclo de ensayo de la comunidad europea, homologadas en el mtt.Más información: www.Consumovehicular.Cl para ver los términos y condiciones de los modelos y sus versiones, diríjase a los términos y condiciones generales en cada modelo publicado o en la siguiente sección: www.Toyota.Cl / terminos - y - condiciones - comerciales - por - modelo /",
                "(**) Acceda a precios preferenciales en el valor de las mantenciones a través de su compra online en tienda.Toyota.Cl.Precios preferenciales con rebajas de hasta 20 % sobre el precio referencial comprando 4 o más mantenciones.Ejemplo: valor referencial cuatro mantenciones(10k, 20k, 30k, 40k) rav4 = $.1089.960 valor compra online = $871.968.Precios preferenciales no aplican para otras formas ni canales de pago.Para conocer precios preferenciales por compras online de mantenciones individuales o paquetes, consulte en tienda.Toyota.Cl.El precio de cada mantención prepagada(mpp) incluye iva, repuestos, insumos, mano de obra.No incluyen reparaciones, trabajos ni cambio de piezas fuera del plan de mantenimiento del vehículo.El plan de mantenimiento se encuentra en el manual de garantía y servicio del vehículo.",
            ],
        },
    };

    return (
        <>
            {isLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}
            {isSuccess && !isLoading && (
                <>
                    <Breadcrumbs {...BreadcrumbsProps} />
                    <section className="cart">
                        {/* Cart empty */}
                        {cartItems.length <= 0 && (
                            <div className="cart__empty">
                                <TitleAndButton
                                    title={data.title}
                                    buttons={[]}
                                />

                                <div className="cart__empty__content">
                                    <img
                                        src={
                                            "/static/website/images/carrito.svg"
                                        }
                                        alt=""
                                    />

                                    <p>Aún no agregaste nada a tu carrito</p>

                                    <Button
                                        text="Empezar a comprar"
                                        to="/mantenciones"
                                        btnStyle="outline"
                                        type="button-link"
                                        color="black"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Cart with items */}
                        {cartItems.length > 0 && (
                            <>
                                <div className="cart__grid wrapper">
                                    <div className="box-1">
                                        <TitleAndButton
                                            title={"Finaliza tu compra"}
                                            buttons={[]}
                                        />

                                        <CartAddedItemContainer
                                            items={cartItems}
                                            canEdit={false}
                                        />

                                        {hasDiscounts &&
                                            <div className="checkout__discount">
                                                <p className="checkout__discount__detail">
                                                    <img className="desktop-img" src={`${window._env_.STATIC_HOST}/static/website/images/maintenance-3.png`} />
                                                    <img className="mobile-img" src={`${window._env_.STATIC_HOST}/static/website/images/icon-maintenance-circle.svg`} />

                                                    <span>{`Descuento por cantidad de mantenciones (${cart.discount.percentage}%)`}</span>
                                                </p>
                                                <p className="checkout__discount__price">
                                                    {`- $ ${formatPrice(cart.discount.price)}`}
                                                </p>
                                            </div>
                                        }

                                        <div className="checkout__total">
                                            <Button
                                                tag={"link"}
                                                text={"Modificar compra"}
                                                url={"/carrito"}
                                            />

                                            <div className="checkout__total__container">
                                                <p>
                                                    TOTAL{" "}
                                                    <span className="checkout__total__quantity">
                                                        ({cartItemsQuantity}{" "}
                                                        unidades)
                                                    </span>{" "}
                                                    <span className="checkout__total__price">
                                                        $
                                                        {formatPrice(cartTotal)}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="box-2">
                                        <div className="checkout__item__list">
                                            <CheckoutForm
                                                totalPrice={formatPrice(
                                                    cartTotal
                                                )}
                                                isVinOptional={checkForTypes(
                                                    cart?.items
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <LegalInformation
                                    texts={data.legalInfo.texts}
                                />
                            </>
                        )}
                    </section>
                </>
            )}
            {isError && <p>Hubo un error</p>}
        </>
    );
};

export default CheckoutPage;
